/**
 * A reply cut into its rounds, the way it happened: each call to the model's
 * thinking, then what it wrote, then what it looked up, called and made. Pure,
 * so it is tested rather than watched.
 */

import { COMPUTER_TOOL_PREFIX } from '@wire';
import type { ReplyRound, ToolRun } from '../api';
import type { LiveImage } from '../hooks/useTurns';

type Range = readonly [from: number, to: number];

export interface RoundView {
  reasoning: string;
  seconds: number | null;
  text: string;
  /** Into the reply's own lists. */
  lookups: Range;
  calls: Range;
  images: Range;
}

/** A reply's parts, whole: how long each list is, and how long the last
 *  round thought. */
export interface ReplyParts {
  reasoning: string;
  text: string;
  lookups: number;
  calls: number;
  images: number;
  thoughtSeconds: number | null;
}

/**
 * One view per round that ended, and one more for what came after the last
 * of them: the round still being written, or nothing on a stored reply. With
 * no rounds, the whole reply is that one view. An end past what the reply
 * holds (a reply cut short) stops at what it holds.
 */
export function roundViews(rounds: readonly ReplyRound[], whole: ReplyParts): RoundView[] {
  const last: ReplyRound = {
    reasoning: whole.reasoning.length,
    text: whole.text.length,
    lookups: whole.lookups,
    calls: whole.calls,
    images: whole.images,
    ...(whole.thoughtSeconds === null ? {} : { thoughtSeconds: whole.thoughtSeconds })
  };
  let from = { reasoning: 0, text: 0, lookups: 0, calls: 0, images: 0 };
  return [...rounds, last].map((end) => {
    const to = (k: keyof typeof from) => Math.max(from[k], Math.min(end[k], last[k]));
    const next = { reasoning: to('reasoning'), text: to('text'), lookups: to('lookups'), calls: to('calls'), images: to('images') };
    const view: RoundView = {
      reasoning: whole.reasoning.slice(from.reasoning, next.reasoning).trim(),
      seconds: end.thoughtSeconds ?? null,
      text: whole.text.slice(from.text, next.text).trim(),
      lookups: [from.lookups, next.lookups],
      calls: [from.calls, next.calls],
      images: [from.images, next.images]
    };
    from = next;
    return view;
  });
}

/**
 * Rounds in a row that only worked a window — no words, lookups or images,
 * and every call a computer call — joined into one, so a task of fifty clicks
 * reads as one group rather than fifty rows. `last` is the index, in `views`,
 * of the last round a view holds; a round something was sent after (`breaks`)
 * is never joined to the next, so what was sent stays where it was.
 */
export function joinComputerRounds(
  views: readonly RoundView[],
  calls: readonly (ToolRun | undefined)[],
  breaks: ReadonlySet<number> = new Set()
): Array<RoundView & { last: number }> {
  const out: Array<RoundView & { last: number; computer: boolean }> = [];
  views.forEach((v, i) => {
    const ran = calls.slice(...v.calls);
    const computer =
      !v.text &&
      v.lookups[0] === v.lookups[1] &&
      v.images[0] === v.images[1] &&
      ran.length > 0 &&
      ran.every((c) => !!c && c.tool.startsWith(COMPUTER_TOOL_PREFIX));
    const prev = out[out.length - 1];
    if (computer && prev?.computer && !breaks.has(prev.last)) {
      out[out.length - 1] = {
        ...prev,
        reasoning: [prev.reasoning, v.reasoning].filter(Boolean).join('\n\n'),
        seconds: prev.seconds !== null && v.seconds !== null ? prev.seconds + v.seconds : (prev.seconds ?? v.seconds),
        calls: [prev.calls[0], v.calls[1]],
        lookups: [prev.lookups[0], v.lookups[1]],
        images: [prev.images[0], v.images[1]],
        last: i
      };
      return;
    }
    out.push({ ...v, last: i, computer });
  });
  return out.map(({ computer: _computer, ...v }) => v);
}

/** Nothing of the round has arrived but its thinking. */
export const emptyRound = (r: RoundView): boolean =>
  !r.text && r.lookups[0] === r.lookups[1] && r.calls[0] === r.calls[1] && r.images[0] === r.images[1];

/** Something in a live reply is running or waiting, and its row says so. */
export function busy(
  calls: readonly (ToolRun | undefined)[],
  lookupsRunning: readonly number[],
  images: readonly (LiveImage | undefined)[]
): boolean {
  return (
    lookupsRunning.length > 0 ||
    calls.some((c) => !!c && (c.state === 'running' || c.state === 'waiting' || c.state === 'approval')) ||
    images.some((i) => !!i && !i.fileId && !i.failed)
  );
}

/** How long a live reply may go with nothing arriving before its end says it
 *  is still at work: a model writing a long call's arguments shows nothing. */
export const QUIET_MS = 2000;
